<script>
  import ErrorBanner from './ErrorBanner.svelte';
  import EmptyState from './EmptyState.svelte';
  import InfoHint from './InfoHint.svelte';
  import Spinner from './Spinner.svelte';
  import { companiesApi, userRolesApi } from '../stores/api.js';
  import { authStore } from '../stores/auth.js';
  import { isAdmin } from '../stores/roles.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { UserPlus, UserMinus, Users as UsersIcon } from '../lib/icons/index.js';

  /**
   * Pairing between accounts and ONE company — the second permission layer.
   *
   * This is the layer whose absence made a freshly created company look
   * broken: the company exists, the account exists, and the company simply
   * never appears in the switcher because no row pairs the two. Nothing in
   * the product said so until this panel existed.
   *
   * Authorization mirrors the backend exactly rather than guessing:
   *  - GET  /companies/:id/users answers every member, but it is redacted by
   *    role: an admin of this company gets the whole roster, anyone else gets
   *    only their own pairing (src/api/companies.js). Colleagues' email
   *    addresses and user ids are not a member-level read;
   *  - POST/PUT/DELETE require `companyRole === 'admin'`
   *    (src/auth/company-context.js + src/api/companies.js), which we read
   *    off the caller's own row in the list we just fetched — a global admin
   *    is granted 'admin' by the middleware even without a row, so they pass
   *    too. That row is always present in the redacted answer, so `canManage`
   *    is decided correctly on both sides of the redaction.
   *
   * The account picker comes from GET /api/user-roles/users because POST
   * /companies/:id/users wants a raw supertokensUserId. That endpoint is
   * global-admin-only, so a company admin who is not an installation admin
   * gets the manual-id fallback instead of a dead end.
   */

  export let companyId = '';

  // The roles the backend accepts on a company pairing, most to least
  // powerful (src/api/companies.js validRoles).
  const COMPANY_ROLES = ['admin', 'operator', 'commercialista', 'readonly'];
  // Roles that may write inside a company, on each side of the fence. The
  // frontend hides its buttons on the global role; the backend refuses the
  // save on the company role. When the two disagree the user meets one of the
  // two confusing halves, so the mismatch is called out explicitly.
  const GLOBAL_WRITE_ROLES = ['admin', 'operator'];
  const COMPANY_WRITE_ROLES = ['admin', 'operator'];

  let members = [];
  let accounts = null; // null = not loaded / not permitted
  let loading = true;
  let error = null;
  let busyUserId = '';
  let adding = false;
  let showAddForm = false;
  let newUserId = '';
  let newRole = 'operator';
  let manualId = false;

  $: myUserId = $authStore.user?.id || null;
  $: if (companyId) load(companyId);

  $: myMembership = members.find(m => m.supertokensUserId === myUserId) || null;
  // Global admins are treated as company admins by company-context(), so they
  // manage pairings even where they have no row of their own.
  $: canManage = $isAdmin || myMembership?.role === 'admin';
  $: candidates = (accounts || []).filter(a => !members.some(m => m.supertokensUserId === a.id));

  async function load(id) {
    loading = true;
    error = null;
    try {
      members = await companiesApi.listUsers(id);
    } catch (err) {
      members = [];
      error = err;
      loading = false;
      return;
    }
    // Best effort: the account directory is admin-only, and not having it is
    // a normal situation rather than a failure — it only costs us the picker.
    try {
      const res = await userRolesApi.getAllUsers();
      accounts = (res?.users || []).slice().sort((a, b) => (a.email || a.id).localeCompare(b.email || b.id));
    } catch {
      accounts = null;
      manualId = true;
    }
    loading = false;
  }

  function roleLabel(role) {
    return tImmediate(`users.roles.${role}`, undefined, role);
  }

  function accountLabel(userId) {
    const match = (accounts || []).find(a => a.id === userId);
    return match?.email || null;
  }

  function memberLabel(member) {
    return member.email || accountLabel(member.supertokensUserId) || member.supertokensUserId;
  }

  /** Global roles of a member, when the account directory is available. */
  function globalRolesOf(member) {
    const match = (accounts || []).find(a => a.id === member.supertokensUserId);
    return match ? (match.roles || []) : null;
  }

  /**
   * Which half of the two-layer mismatch this member is living in, or '' when
   * the two layers agree (or we cannot tell).
   */
  function mismatchKey(member) {
    const globalRoles = globalRolesOf(member);
    if (!globalRoles) return '';
    const globalWrite = globalRoles.some(r => GLOBAL_WRITE_ROLES.includes(r));
    const companyWrite = COMPANY_WRITE_ROLES.includes(member.role);
    if (globalWrite === companyWrite) return '';
    return globalWrite ? 'seesButtonsCannotSave' : 'canSaveButNoButtons';
  }

  async function handleAdd(e) {
    e.preventDefault();
    const supertokensUserId = newUserId.trim();
    if (!supertokensUserId) return;
    adding = true;
    error = null;
    try {
      await companiesApi.inviteUser(companyId, { supertokensUserId, role: newRole });
      await load(companyId);
      showAddForm = false;
      newUserId = '';
      newRole = 'operator';
    } catch (err) {
      error = err;
    } finally {
      adding = false;
    }
  }

  /**
   * The <select> element is passed in rather than just its value so a refusal
   * can be undone on the element itself. Re-assigning `members` would not do
   * it: the row is keyed, the role string is unchanged, so Svelte sees nothing
   * to update and the dropdown would keep showing a role the server rejected.
   */
  async function handleRoleChange(member, select) {
    const role = select.value;
    if (role === member.role) return;
    busyUserId = member.supertokensUserId;
    error = null;
    try {
      await companiesApi.updateUserRole(companyId, member.supertokensUserId, role);
      await load(companyId);
    } catch (err) {
      error = err;
      select.value = member.role;
    } finally {
      busyUserId = '';
    }
  }

  async function handleRemove(member) {
    const isSelf = member.supertokensUserId === myUserId;
    const question = isSelf
      ? tImmediate('companySettings.members.confirmRemoveSelf')
      : tImmediate('companySettings.members.confirmRemove', { account: memberLabel(member) });
    if (!confirm(question)) return;
    busyUserId = member.supertokensUserId;
    error = null;
    try {
      await companiesApi.removeUser(companyId, member.supertokensUserId);
      await load(companyId);
    } catch (err) {
      error = err;
    } finally {
      busyUserId = '';
    }
  }
</script>

<div class="card p-6">
  <h2 class="eyebrow eyebrow-lg mb-1 flex items-center gap-2" style="color: var(--color-primary);">
    <UsersIcon size={16} />
    {$t('companySettings.members.title')}
    <InfoHint text={$t('companySettings.members.titleHint')} />
  </h2>
  <p class="text-xs mb-4 max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.members.intro')}</p>

  <ErrorBanner {error} />

  {#if loading}
    <Spinner label={$t('companySettings.members.loading')} padding="py-8" />
  {:else}
    {#if members.length === 0}
      <div class="border-w" style="border-color: var(--color-tint-medium);">
        <EmptyState icon={UsersIcon} title={$t('companySettings.members.emptyTitle')} body={$t('companySettings.members.emptyBody')} />
      </div>
    {:else}
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b-w" style="border-color: var(--color-tint-medium);">
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.members.columns.account')}</th>
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.members.columns.companyRole')}</th>
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.members.columns.globalRole')}</th>
              <th class="text-right py-3 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {#each members as member (member.supertokensUserId)}
              {@const globalRoles = globalRolesOf(member)}
              {@const warning = mismatchKey(member)}
              <tr class="border-b" style="border-color: var(--color-tint-soft);">
                <td class="py-2 px-2 align-top">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-bold break-all" style="color: var(--color-text-heading);">{memberLabel(member)}</span>
                    {#if member.supertokensUserId === myUserId}
                      <span class="px-2 py-0.5 eyebrow" style="color: var(--color-primary); background: var(--color-tint-soft);">{$t('users.youBadge')}</span>
                    {/if}
                  </div>
                  <p class="text-xs font-mono mt-1 break-all" style="color: var(--color-text-muted);">{member.supertokensUserId}</p>
                  {#if warning}
                    <p class="text-xs mt-1 max-w-md" style="color: var(--color-secondary);">{$t(`companySettings.members.mismatch.${warning}`)}</p>
                  {/if}
                </td>
                <td class="py-2 px-2 align-top">
                  {#if canManage}
                    <label class="sr-only" for="member-role-{member.supertokensUserId}">{$t('companySettings.members.roleLabel', { account: memberLabel(member) })}</label>
                    <select id="member-role-{member.supertokensUserId}" value={member.role} disabled={busyUserId === member.supertokensUserId}
                      on:change={(e) => handleRoleChange(member, e.currentTarget)}
                      class="input input-compact w-auto">
                      {#each COMPANY_ROLES as role (role)}
                        <option value={role}>{roleLabel(role)}</option>
                      {/each}
                    </select>
                  {:else}
                    <span class="eyebrow" style="color: var(--color-primary);">{roleLabel(member.role)}</span>
                  {/if}
                  <p class="text-xs mt-1 max-w-xs" style="color: var(--color-text-muted);">{$t(`users.roleHints.${member.role}`, undefined, '')}</p>
                </td>
                <td class="py-2 px-2 align-top text-xs" style="color: var(--color-text-muted);">
                  {#if globalRoles === null}
                    <span>{$t('companySettings.members.globalRoleUnknown')}</span>
                  {:else if globalRoles.length === 0}
                    <span>{$t('users.noRoles')}</span>
                  {:else}
                    {globalRoles.map(roleLabel).join(', ')}
                  {/if}
                </td>
                <td class="py-2 px-2 text-right align-top">
                  {#if canManage}
                    <button type="button" class="p-1 hover:text-danger transition-colors" style="color: var(--color-text-muted);"
                      disabled={busyUserId === member.supertokensUserId}
                      title={$t('companySettings.members.remove')}
                      aria-label={$t('companySettings.members.removeLabel', { account: memberLabel(member) })}
                      on:click={() => handleRemove(member)}>
                      <UserMinus size={16} />
                    </button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    {#if canManage}
      {#if showAddForm}
        <form on:submit={handleAdd} class="mt-4 p-4 border-w space-y-3" style="border-color: var(--color-tint-medium);">
          <p class="eyebrow" style="color: var(--color-primary);">{$t('companySettings.members.addTitle')}</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="member-add-account" class="block eyebrow mb-1" style="color: var(--color-text-muted);">
                {$t('companySettings.members.accountLabel')}
                <InfoHint text={$t('companySettings.members.accountHint')} />
              </label>
              {#if manualId}
                <input id="member-add-account" bind:value={newUserId} required placeholder={$t('companySettings.members.manualIdPlaceholder')}
                  class="input" />
                <p class="text-xs mt-1" style="color: var(--color-text-muted);">{$t('companySettings.members.manualIdHint')}</p>
              {:else}
                <select id="member-add-account" bind:value={newUserId} required
                  class="input">
                  <option value="" disabled>{$t('companySettings.members.accountPlaceholder')}</option>
                  {#each candidates as account (account.id)}
                    <option value={account.id}>{account.email || account.id}</option>
                  {/each}
                </select>
                {#if candidates.length === 0}
                  <p class="text-xs mt-1" style="color: var(--color-text-muted);">{$t('companySettings.members.noCandidates')}</p>
                {/if}
                <button type="button" class="eyebrow mt-1 hover:text-action-strong" style="color: var(--color-text-muted);"
                  on:click={() => { manualId = true; newUserId = ''; }}>
                  {$t('companySettings.members.useManualId')}
                </button>
              {/if}
            </div>
            <div>
              <label for="member-add-role" class="block eyebrow mb-1" style="color: var(--color-text-muted);">
                {$t('companySettings.members.newRoleLabel')}
              </label>
              <select id="member-add-role" bind:value={newRole}
                class="input">
                {#each COMPANY_ROLES as role (role)}
                  <option value={role}>{roleLabel(role)}</option>
                {/each}
              </select>
              <p class="text-xs mt-1 max-w-xs" style="color: var(--color-text-muted);">{$t(`users.roleHints.${newRole}`, undefined, '')}</p>
            </div>
          </div>
          <div class="flex gap-3">
            <button type="submit" disabled={adding || !newUserId.trim()}
              class="btn btn-primary btn-sm">
              {adding ? $t('companySettings.members.adding') : $t('companySettings.members.addAction')}
            </button>
            <button type="button" on:click={() => { showAddForm = false; newUserId = ''; }}
              class="btn btn-secondary btn-sm">
              {$t('common.cancel')}
            </button>
          </div>
        </form>
      {:else}
        <button type="button" on:click={() => (showAddForm = true)}
          class="btn btn-secondary btn-sm mt-4 gap-2">
          <UserPlus size={14} /> {$t('companySettings.members.add')}
        </button>
      {/if}
    {:else}
      <p class="text-xs mt-4 max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.members.readOnlyNote')}</p>
    {/if}
  {/if}
</div>
